import { Box, CircularProgress, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Iconify } from 'src/components/iconify';
import { XButton } from '../../components/XButton';
import { appVersionDataProvider } from './dataProvider';
import { AppVersionFormValues, AppVersionInfo } from './types';
import DetailForm from './DetailForm';
import PreviewDialog from './PreviewDialog';
import { Resources } from '../resource';

const DEFAULT_VALUES: AppVersionFormValues = {
  versionCode: '',
  platform: 'IOS',
  minVersion: '',
  showFrequency: 'ONCE_PER_DAY',
  forceUpdate: false,
  active: true,
  locales: [
    { locale: 'ZH_HANS', title: '', releaseNotes: [''], button: '' },
    { locale: 'EN', title: '', releaseNotes: [''], button: '' },
  ],
};

export default function AppVersionCreate() {
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();

  const [activeRecords, setActiveRecords] = useState<AppVersionInfo[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const methods = useForm<AppVersionFormValues>({ defaultValues: DEFAULT_VALUES });
  const { handleSubmit, watch } = methods;

  const versionCode = watch('versionCode');
  const locales = watch('locales');

  useEffect(() => {
    appVersionDataProvider
      .getList('', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'updatedAt', order: 'DESC' },
        filter: {},
      })
      .then(({ data }) => {
        const actives = data.filter((r: AppVersionInfo) => r.active);
        setActiveRecords(actives);
      })
      .catch(() => {});
  }, []);

  const onSubmit = async (values: AppVersionFormValues) => {
    setSaving(true);
    try {
      await appVersionDataProvider.create('', { data: values });
      notify(translate('module.appVersionManagement.notify.createSuccess'), { type: 'success' });
      navigate(`/${Resources.appVersionManagement}`);
    } catch (e: any) {
      notify(e?.message || translate('module.appVersionManagement.notify.createError'), {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionGuard resource={'app-version-updates'} action="create" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.appVersionManagement.createTitle')}
          links={[
            {
              name: translate('module.appVersionManagement.breadcrumb.list'),
              href: `/${Resources.appVersionManagement}`,
            },
            { name: translate('module.appVersionManagement.breadcrumb.create') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <XButton
                variant="outlined"
                color="inherit"
                label={translate('module.appVersionManagement.previewDialog')}
                onClick={() => setPreviewOpen(true)}
                sx={{ minWidth: 150 }}
              >
                <Iconify icon="eva:eye-outline" sx={{ mr: 0.5 }} />
              </XButton>
              <XButton
                variant="contained"
                label={
                  saving
                    ? translate('module.appVersionManagement.saving')
                    : translate('module.appVersionManagement.save')
                }
                disabled={saving}
                onClick={handleSubmit(onSubmit)}
              />
            </Stack>
          }
        />

        <FormProvider {...methods}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <DetailForm
              activeByPlatform={Object.fromEntries(
                activeRecords.map((r) => [r.platform, r.versionCode])
              )}
            />
          </Box>
        </FormProvider>

        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          versionCode={versionCode}
          locales={locales}
          forceUpdate={watch('forceUpdate')}
          displayType="side"
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
